import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useVoltarAuditorias } from '@/modules/system/auditorias/use-voltar-auditorias';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import type { ReadUsuarioDto } from '@/api.types';
import { GetAuditoriaTasks } from './auditoria-tasks.service';
import type { AuditoriaTaskDto, FindAuditoriaTasksDto } from './auditoria-tasks.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

export default function LogsTasksPage() {
  useVoltarAuditorias();
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const [loaded, setLoaded] = useState(false);
  const [buscando, setBuscando] = useState(false);

  const [dataInicial, setDataInicial] = useState(primeiroDia);
  const [dataFinal, setDataFinal] = useState(ultimoDia);
  const [tarefa, setTarefa] = useState('');
  const [codigo, setCodigo] = useState('');
  const [idUsuario, setIdUsuario] = useState<number>(0);
  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);

  const [registros, setRegistros] = useState<[AuditoriaTaskDto[], number]>();

  const tableState = useDataTableState('executadoEm');

  useEffect(() => {
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        const ordenados = result[0].sort((a, b) => a.username!.localeCompare(b.username!));
        setListaUsuarios(ordenados);
      })
      .catch((e) => toast.error(String(e)))
      .finally(() => setLoaded(true));
  }, []);

  const columns: Column<AuditoriaTaskDto>[] = useMemo(
    () => [
      {
        key: 'executadoEm',
        header: 'Data',
        render: (row) => format(new Date(row.executadoEm), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
      },
      { key: 'tarefa', header: 'Tarefa' },
      { key: 'codigo', header: 'Código' },
      {
        key: 'usuario.username',
        header: 'Remetente (usuário)',
        render: (row) => row.usuario?.username || 'Sistema',
      },
    ],
    [],
  );

  async function handleBuscar() {
    if (!loaded) return;

    setBuscando(true);
    try {
      const filters: FindAuditoriaTasksDto = {
        skip: (tableState.currentPage - 1) * tableState.pageSize,
        take: tableState.pageSize,
        data_inicial: dataInicial.toISOString(),
        data_final: dataFinal.toISOString(),
        tarefa: tarefa.trim() || undefined,
        codigo: codigo.trim() || undefined,
        usuario_id: idUsuario || undefined,
      };

      const result = await GetAuditoriaTasks(filters);
      setRegistros(result);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBuscando(false);
    }
  }

  useEffect(() => {
    void handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, tableState.currentPage, tableState.pageSize, tableState.sortConfig]);

  return (
    <div className="row">
      <div className="col-12">
        <h3>Logs de Tarefas</h3>
        <span className="blockquote-footer pb-4 text-end">Auditoria de execução de tasks (crons) da API.</span>
      </div>
      <hr />

      {registros && (
        <div className="col-12">
          <DataTable
            columns={columns}
            data={registros[0]}
            onChange={handleBuscar}
            serverSide={true}
            showFilter={false}
            states={tableState}
            totalRecords={registros[1]}
            renderFilters={() => (
              <div className="row">
                <div className="col-md-3">
                  <label htmlFor="usuario-task" className="col-form-label">
                    Usuário:
                  </label>
                  <select
                    id="usuario-task"
                    className="form-select"
                    value={idUsuario}
                    onChange={(e) => setIdUsuario(Number(e.target.value))}
                  >
                    <option value={0}>Sistema</option>
                    {listaUsuarios.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-5">
                  <label htmlFor="tarefa-task" className="col-form-label">
                    Tarefa contém:
                  </label>
                  <input
                    id="tarefa-task"
                    className="form-control"
                    value={tarefa}
                    onChange={(e) => setTarefa(e.target.value)}
                    placeholder="ex.: folhas / prazo / processos seletivos"
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="codigo-task" className="col-form-label">
                    Código:
                  </label>
                  <input
                    id="codigo-task"
                    className="form-control font-monospace"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    placeholder="SUCESSO / FALHA / DESATIVADO"
                  />
                </div>

                <div className="col-md-1">
                  <label htmlFor="data-inicial-task" className="col-form-label">
                    Inicial:
                  </label>
                  <input
                    id="data-inicial-task"
                    type="date"
                    className="form-control"
                    value={dataInicial.toISOString().slice(0, 10)}
                    onChange={(e) => setDataInicial(new Date(e.target.value))}
                  />
                </div>

                <div className="col-md-1">
                  <label htmlFor="data-final-task" className="col-form-label">
                    Final:
                  </label>
                  <input
                    id="data-final-task"
                    type="date"
                    className="form-control"
                    value={dataFinal.toISOString().slice(0, 10)}
                    onChange={(e) => setDataFinal(new Date(e.target.value))}
                  />
                </div>

                <div className="col-12 mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleBuscar}
                    disabled={buscando}
                  >
                    {buscando ? 'Buscando...' : 'Pesquisar'}
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}

